import {
  Alert,
  Button,
  Divider,
  Grid,
  Select,
  Stack,
  Text,
} from '@mantine/core'

import { IconInfoCircle } from '@tabler/icons-react'

import { type DeviceBase } from '@data/device/model/device'
import {
  useEffect,
  useRef,
  useState,
} from 'react'
import { ReadOnlyField } from '@components/ui/form/ReadOnlyField'
import { hasPermissions } from '@common/utils/validation/authValidators'
import { TokenPermission } from '@data/rbac/model/rbac'

// Takes the send handlers rather than calling the mutations itself. Provisioned and test
// devices send the same two commands through different endpoints — /devices/{d_uuid} with an
// idType, /test_devices/{device_id} without — and the tab is otherwise identical for both.
export interface DeviceCommandsTabProps {
  device: DeviceBase
  deviceSendingMessage: boolean
  permissions: TokenPermission[]
  firmwareOptions: string[]
  onFirmwareUpdate: (binFileName: string) => void
  onReset: () => void
}

export const DeviceCommandsTab = ({
                                    device,
                                    deviceSendingMessage,
                                    permissions,
                                    firmwareOptions,
                                    onFirmwareUpdate,
                                    onReset,
                                  }: DeviceCommandsTabProps) => {
  const [selectedFirmware, setSelectedFirmware] = useState<string>('')
  const [resetCooldown, setResetCooldown] = useState(0)
  const resetIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    return () => {
      if (resetIntervalRef.current) clearInterval(resetIntervalRef.current)
    }
  }, [])

  const canUpdateFirmware =
    device.deviceVendor === 'SMARTFLOW' &&
    hasPermissions(permissions, [
      'DEVICE:FIRMWARE:UPDATE',
    ])

  const canResetDevice =
    device.deviceVendor === 'SMARTFLOW' &&
    hasPermissions(permissions, [
      'DEVICE:RESET',
    ])

  const handleFirmwareUpdate = () => {
    if (!selectedFirmware) {
      return
    }

    onFirmwareUpdate(selectedFirmware)
  }

  const handleReset = () => {
    onReset()
    setResetCooldown(30)
    resetIntervalRef.current = setInterval(() => {
      setResetCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(resetIntervalRef.current!)
          return 0
        }
        return prev - 1
      })
    }, 1_000)
  }
  return (
    <Stack gap="lg">
      {canUpdateFirmware && (
        <>
          <Stack gap="md">
            <Text fw={600}>
              Firmware Update
            </Text>
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <ReadOnlyField
                  label="Current FW"
                  value={device.firmware || 'Unknown'}
                  enableClipboard
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Firmware Version"
                  placeholder="Select firmware"
                  value={selectedFirmware}
                  onChange={(value) =>
                    setSelectedFirmware(
                      value || '',
                    )
                  }
                  data={firmwareOptions.map(
                    (fw) => ({
                      value: fw,
                      label: fw,
                    }),
                  )}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <ReadOnlyField
                  label="Current Bin File"
                  value={device.binFileName || ''}
                  enableClipboard
                />
              </Grid.Col>
            </Grid>

            <Button
              onClick={handleFirmwareUpdate}
              loading={deviceSendingMessage && !resetCooldown}
              disabled={
                !selectedFirmware ||
                deviceSendingMessage ||
                !!resetCooldown ||
                selectedFirmware === device.binFileName
              }
            >
              Send Firmware Update
            </Button>

            <Alert
              icon={
                <IconInfoCircle size={16} />
              }
              variant="light"
            >
              Remember to reset the
              device after sending a firmware
              updates. Allow at least
              two minutes for the device to reconnect.
            </Alert>
          </Stack>

          <Divider />
        </>
      )}

      {canResetDevice && (
        <>
          <Stack gap="md">
            <Text fw={600}>
              Reset Device
            </Text>

            <Button
              color="red"
              onClick={handleReset}
              disabled={!!resetCooldown}
            >
              {resetCooldown ? `Reset Device (${resetCooldown}s)` : 'Reset Device'}
            </Button>
          </Stack>

          <Divider />
        </>
      )}
    </Stack>
  )
}
